<template>
    <section
      ref="sectionRef"
      class="pricing-banner"
      :class="{
        'pricing-banner--visible': isVisible
      }"
    >
      <div class="pricing-banner__inner">
        <!-- Title -->
        <h2 class="pricing-banner__title">
          Цено<span>образование</span>
        </h2>
  
        <!-- Visual -->
        <div class="pricing-banner__visual">
          <div class="ambient ambient--left"></div>
          <div class="ambient ambient--right"></div>
  
          <img
            :src="currentImage"
            class="pricing-banner__image"
            alt="Ценообразование HR3"
            draggable="false"
          />


          <div class="pricing-analysis">
            <span class="pricing-analysis__label">
              Тариф HR3
            </span>

            <h2 class="pricing-analysis__title">
              Больше возможностей за одну понятную цену
            </h2>

            <div class="pricing-analysis__content">
              <p>
                Мы изучили предложения рекрутинговых сервисов,
                площадок и Telegram-каналов и постарались сделать
                условия, при которых компания платит не за каждое
                отдельное действие, а получает полноценный инструмент
                для найма.
              </p>

              <p>
                За <strong>3 990 ₽ в месяц</strong> вы получаете
                неограниченное создание вакансий и мероприятий,
                работу с откликами и доступ ко всему AI-функционалу
                HR3 — от анализа кандидатов до оценки мэтча
                и интеллектуальной сортировки откликов.
              </p>

              <p>
                На многих площадках несколько тысяч рублей может
                стоить только размещение одной вакансии.
                В HR3 за сопоставимую стоимость вы получаете
                целый месяц работы со всеми инструментами сервиса.
              </p>

              <p class="pricing-analysis__result">
                Одна подписка. Неограниченные вакансии и ивенты.
                Весь AI. <strong>3 990 ₽ в месяц.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  
    <div class="separator"></div>
  </template>
  
  <script setup>
  import {
    computed,
    onMounted,
    onUnmounted,
    ref
  } from "vue";
  
  const lightImage = new URL(
    "../../assets/PricingBanner-light.png",
    import.meta.url
  ).href;
  
  const darkImage = new URL(
    "../../assets/PricingBanner-dark.png",
    import.meta.url
  ).href;
  
  const sectionRef = ref(null);
  const isVisible = ref(false);
  
  const theme = ref(
    document.documentElement.getAttribute(
      "data-theme"
    ) || "light"
  );
  
  const currentImage = computed(() => {
    return theme.value === "dark"
      ? darkImage
      : lightImage;
  });
  
  let themeObserver = null;
  let intersectionObserver = null;
  
  onMounted(() => {
    themeObserver = new MutationObserver(() => {
      theme.value =
        document.documentElement.getAttribute(
          "data-theme"
        ) || "light";
    });
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );
  
    intersectionObserver =
      new IntersectionObserver(
        entries => {
          const entry = entries[0];
  
          if (!entry?.isIntersecting) {
            return;
          }
  
          isVisible.value = true;
          intersectionObserver?.disconnect();
        },
        {
          threshold: 0.12
        }
      );
  
    if (sectionRef.value) {
      intersectionObserver.observe(
        sectionRef.value
      );
    } else {
      requestAnimationFrame(() => {
        isVisible.value = true;
      });
    }
  });
  
  onUnmounted(() => {
    themeObserver?.disconnect();
    intersectionObserver?.disconnect();
  });
  </script>
  
  <style scoped>
  * {
    box-sizing: border-box;
  }
  
  .pricing-banner {
    position: relative;
  
    width: 100%;
  
    overflow: hidden;
  
    background: var(--background);
  
    opacity: 0;
    transform: translateY(18px);
  
    transition:
      opacity 0.75s ease,
      transform 0.75s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }
  
  .pricing-banner--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .pricing-banner__inner {
    width: 100%;
    max-width: 1440px;
  
    margin: 0 auto;
  
    padding:
      78px
      64px
      0px;
  }
  
  /* Title */
  
  .pricing-banner__title {
    position: relative;
    z-index: 5;
  
    margin:
      0
      0
      22px;
  
    color: var(--text);
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      clamp(
        42px,
        4.3vw,
        64px
      );
  
    line-height: 1;
    letter-spacing: -0.045em;
  }
  
  .pricing-banner__title span {
    color: #2277e6;
  }
  
  .pricing-banner__visual {
  position: relative;

  width: 100%;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  isolation: isolate;
}
  
  .pricing-banner__image {
    position: relative;
    z-index: 2;
  
    width: min(
      900px,
      100%
    );
  
    height: auto;
    display: block;
    object-fit: contain;
    user-select: none;
    opacity: 0;
  
    transform:
      translateY(16px)
      scale(0.985);
  
    filter:
      drop-shadow(
        0
        28px
        55px
        rgba(
          32,
          67,
          120,
          0.07
        )
      );
  
    transition:
      opacity 0.9s ease 0.12s,
      transform 0.9s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        0.12s;
  }
  
  .pricing-banner--visible
  .pricing-banner__image {
    opacity: 1;
  
    transform:
      translateY(0)
      scale(1);
  }

  .ambient {
    position: absolute;
    z-index: 0;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(120px);
    opacity: 0.11;
    animation:
      pricingAmbient
      16s
      ease-in-out
      infinite;
  }
  
  .ambient--left {
    width: 380px;
    height: 380px;
    left: 12%;
    top: 14%;
    background: #4b8ff7;
  }
  
  .ambient--right {
    width: 320px;
    height: 320px;
    right: 12%;
    bottom: 10%;
    background: #7865ef;
    animation-delay: -7s;
  }
  
  @keyframes pricingAmbient {
    0%,
    100% {
      transform:
        translate3d(
          0,
          0,
          0
        )
        scale(1);
    }
  
    50% {
      transform:
        translate3d(
          18px,
          -15px,
          0
        )
        scale(1.05);
    }
  }
  
  /* Separator */
  
  .separator {
    width: 100%;
    height: 1px;
  
    background:
      var(
        --separator_home,
        rgba(
          0,
          0,
          0,
          0.08
        )
      );
  }
  
  /* Dark */
  
  html[data-theme="dark"]
  .pricing-banner__image {
    filter:
      drop-shadow(
        0
        28px
        60px
        rgba(
          0,
          0,
          0,
          0.22
        )
      );
  }
  
  html[data-theme="dark"]
  .ambient {
    opacity: 0.16;
  }
  
  /* Laptop */
  
  @media (max-width: 1366px) {
    .pricing-banner__inner {
      padding:
        56px
        48px
        38px;
    }
  
    .pricing-banner__title {
      margin-bottom: 14px;
  
      font-size:
        clamp(
          38px,
          4vw,
          54px
        );
    }
  
    .pricing-banner__visual {
      min-height: 760px;
    }
  
    .pricing-banner__image {
      width: min(
        760px,
        100%
      );
    }
  }
  
  /* 13" laptop */
  
  @media (
    min-width: 769px
  ) and (
    max-height: 820px
  ) {
    .pricing-banner__inner {
      padding:
        40px
        46px
        26px;
    }
  
    .pricing-banner__title {
      margin-bottom: 8px;
  
      font-size:
        clamp(
          36px,
          3.7vw,
          48px
        );
    }
  
    .pricing-banner__visual {
      min-height: 610px;
    }
  
    .pricing-banner__image {
      width: min(
        620px,
        100%
      );
    }
  }
  
  /* Tablet */
  
  @media (max-width: 900px) {
    .pricing-banner__inner {
      padding:
        48px
        28px
        32px;
    }
  
    .pricing-banner__title {
      font-size:
        clamp(
          36px,
          7vw,
          50px
        );
    }
  
    .pricing-banner__visual {
      min-height: 0;
    }
  
    .pricing-banner__image {
      width: 88%;
    }
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .pricing-banner__inner {
      padding:
        38px
        18px
        26px;
    }
  
    .pricing-banner__title {
      margin-bottom: 20px;
  
      font-size: 35px;
      line-height: 1.03;
    }
  
    .pricing-banner__visual {
      margin:
        0
        -8px;
    }
  
    .pricing-banner__image {
      width: 105%;
  
      filter: none;
    }
  
    .ambient {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    .pricing-banner__inner {
      padding:
        32px
        16px
        22px;
    }
  
    .pricing-banner__title {
      font-size: 30px;
    }
  
    .pricing-banner__image {
      width: 112%;
    }
  }
  
  /* Reduced motion */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .pricing-banner,
    .pricing-banner__image,
    .ambient {
      animation: none;
      transition: none;
    }
  
    .pricing-banner {
      opacity: 1;
      transform: none;
    }
  
    .pricing-banner__image {
      opacity: 1;
      transform: none;
    }
  }

.pricing-analysis {
  position: absolute;
  z-index: 5;

  top: 450px;
  right: 550px;

  width: 34%;
  max-width: 440px;

  padding: 24px 26px;

  transform: translateY(-44%);

  background:
    rgba(
      255,
      255,
      255,
      0.76
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.72
    );

  border-radius: 20px;

  box-shadow:
    0 18px 50px
    rgba(
      30,
      50,
      80,
      0.07
    );

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  opacity: 0;

  transition:
    opacity 0.8s ease 0.3s,
    transform 0.8s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      0.3s;
}

.pricing-banner--visible
.pricing-analysis {
  opacity: 1;

  transform: translateY(-50%);
}

/* Label */

.pricing-analysis__label {
  display: inline-flex;
  align-items: center;

  margin-bottom: 11px;
  padding: 5px 8px;

  color: #2277e6;

  background:
    rgba(
      34,
      119,
      230,
      0.08
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.1
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.07em;
  text-transform: uppercase;
}

/* Title */

.pricing-analysis__title {
  margin:
    0
    0
    14px;

  color: #20242c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 21px;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

/* Content */

.pricing-analysis__content {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.pricing-analysis__content p {
  margin: 0;

  color: #626a77;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.48;
}

.pricing-analysis__content strong {
  color: #353b45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-weight: normal;
}

/* Result */

.pricing-analysis__result {
  padding:
    12px
    13px !important;

  color:
    #395a88 !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        34,
        119,
        230,
        0.08
      ),
      rgba(
        83,
        104,
        232,
        0.05
      )
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.09
    );

  border-radius: 11px;
}

.pricing-analysis__result strong {
  color: #2277e6;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-weight: normal;
}

/* Dark */

html[data-theme="dark"]
.pricing-analysis {
  background:
    rgba(
      28,
      28,
      30,
      0.8
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 20px 55px
    rgba(
      0,
      0,
      0,
      0.24
    );
}

html[data-theme="dark"]
.pricing-analysis__label {
  color: #72a8ff;

  background:
    rgba(
      63,
      137,
      241,
      0.12
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.16
    );
}

html[data-theme="dark"]
.pricing-analysis__title {
  color: #f2f3f5;
}

html[data-theme="dark"]
.pricing-analysis__content p {
  color: #a7adb7;
}

html[data-theme="dark"]
.pricing-analysis__content strong {
  color: #e1e4e9;
}

html[data-theme="dark"]
.pricing-analysis__result {
  color:
    #9fc3ff !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        40,
        135,
        247,
        0.12
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.14
    );
}

html[data-theme="dark"]
.pricing-analysis__result strong {
  color: #72a8ff;
}

/* Laptop */

@media (max-width: 1366px) {
  .pricing-analysis {
    right: 0;

    width: 35%;
    max-width: 370px;

    padding: 18px 19px;

    border-radius: 16px;
  }

  .pricing-analysis__label {
    margin-bottom: 8px;

    font-size: 8px;
  }

  .pricing-analysis__title {
    margin-bottom: 10px;

    font-size: 17px;
  }

  .pricing-analysis__content {
    gap: 8px;
  }

  .pricing-analysis__content p {
    font-size: 9.5px;
    line-height: 1.42;
  }

  .pricing-analysis__result {
    padding:
      9px
      10px !important;
  }
}

/* 13" laptop */

@media (
  min-width: 769px
) and (
  max-height: 820px
) {
  .pricing-analysis {
    width: 34%;
    max-width: 340px;

    padding: 14px 16px;
  }

  .pricing-analysis__title {
    margin-bottom: 8px;

    font-size: 15px;
  }

  .pricing-analysis__content {
    gap: 6px;
  }

  .pricing-analysis__content p {
    font-size: 8.5px;
    line-height: 1.38;
  }

  .pricing-analysis__result {
    padding:
      7px
      8px !important;
  }
}

/* Tablet / mobile */

@media (max-width: 900px) {
  .pricing-banner__visual {
    flex-direction: column;
  }

  .pricing-analysis {
    position: relative;

    top: auto;
    right: auto;

    width: 100%;
    max-width: 680px;

    margin-top: 18px;
    padding: 18px 20px;

    transform: translateY(10px);
  }

  .pricing-banner--visible
  .pricing-analysis {
    transform: translateY(0);
  }

  .pricing-analysis__title {
    font-size: 18px;
  }

  .pricing-analysis__content p {
    font-size: 11px;
  }
}
  </style>